<template>
  <DashboardSlideOut
    :label="label"
    :show="show"
    :disabled="hasError"
    @cancel="$emit('cancel')"
    @complete="updateFilters"
    class="common-slideout"
  >
    <template v-slot:headerContent>
      <ButtonV02
        v-if="showButton"
        rounded
        large
        :color="restoreDefaultButton.color"
        :whiteText="true"
        :disabled="hasDefaultFilters"
        @click="restoreDefaultFilters"
      >
        {{ restoreDefaultButton.text }}
      </ButtonV02>
    </template>
    <template v-slot:slideOutContent>
      <SearchInput
        v-model="searchTerm"
        class="filters-search"
        name="filters-search"
        placeholder="Search Filters"
      />
      <FiltersSlideOutContent
        v-model="localValue"
        :dimensions="filteredDimensions"
        :useSalesInsightsIfAvailable="isSalesInsights"
        :singleSelectBrandId="localSingleSelectBrandId"
        :selectedCategoryId="localSelectedCategoryId"
        :selectedSubcategoryIds="localSelectedSubcategoryIds"
        @updateSingleSelectBrandId="updateLocalSingleSelectBrandId"
        @updateSelectedCategory="updateSelectedCategory"
        @updateSelectedSubcategories="updateSelectedSubcategories"
        @input="localValue = $event"
        @error="hasError = $event"
      />
      <div
        v-if="!filteredDimensions || !filteredDimensions.length"
        class="no-filters-results"
      >
        No {{ label }}
      </div>
    </template>
  </DashboardSlideOut>
</template>
<script>
import { mapState } from 'vuex';
import { isEqual } from 'lodash';
import { SearchInput, ButtonV02 } from '@mikmak/components';
import DashboardSlideOut from './DashboardSlideOut.vue';
import FiltersSlideOutContent from './FiltersSlideOutContent.vue';
import {
  getAllowedFilters,
  getSelectedFilters,
  removeBrandOnlyFilters,
  removeCategoryOnlyFilters,
  isViewFilterable,
} from '../../../services/dashboard/filtersService.js';
import { getSelectedPortfolioBrands } from '../../../services/dashboard/portfolioBrandsService';

export default {
  props: {
    show: Boolean,
    isSalesInsights: Boolean,
  },
  components: {
    SearchInput,
    DashboardSlideOut,
    FiltersSlideOutContent,
    ButtonV02,
  },
  data() {
    return {
      localValue: [],
      localSingleSelectBrandId: '',
      localSelectedCategoryId: '',
      localSelectedSubcategoryIds: [],
      searchTerm: '',
      label: 'Filters',
      hasError: false,
      restoreDefaultButton: {
        text: 'restore default',
        color: '#4369f4',
      },
    };
  },
  computed: {
    ...mapState({
      activeView: state => state.main.activeView,
      showNewBrandSelector: state => state.main.showNewBrandSelector,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
      allowedFilters: state => state.filters.allowedFilters,
      activeBenchmarksView: state => state.benchmarks.activeBenchmarksView,
      portfolioBrands: state => state.dashboard.portfolioBrands,
      currentBrand: state => state.attachStudio.currentAttachBrand,
      selectedCategory: state => state.dashboard.selectedCategory,
      selectedSubcategories: state => state.dashboard.selectedSubcategories,
    }),
    currentBrandId() {
      return this.currentBrand?.brandId || '';
    },
    currentBrandCategoryId() {
      return this.currentBrand?.categoryId;
    },
    currentBrandSubcategoryIds() {
      return this.currentBrand?.subCategoryIds;
    },
    showButton() {
      return this.isBenchmarksView
        && this.activeBenchmarksView === 'category';
    },
    filteredDimensions() {
      return this.viewAllowedFilters.filter(this.isDimensionInSearch);
    },
    selectedFilters() {
      return getSelectedFilters(
        this.dashboardSelectedFilters,
        this.viewAllowedFilters,
      );
    },
    viewAllowedFilters() {
      if (!isViewFilterable(this.activeView)) return [];

      const filters = getAllowedFilters(this.allowedFilters, this.activeView);

      if (this.isBenchmarksView && this.activeBenchmarksView === 'category') {
        return removeBrandOnlyFilters(filters);
      }

      if (this.isBenchmarksView && this.activeBenchmarksView === 'brand') {
        return removeCategoryOnlyFilters(filters);
      }

      return filters;
    },
    isBenchmarksView() {
      return this.activeView === 'Benchmarks';
    },
    hasDefaultFilters() {
      const supplementalFilters = this.localValue.filter(f => {
        return f.key !== 'category' && f.key !== 'brandName' && f.key !== 'subcategory';
      });

      return supplementalFilters.length === 0
        && this.localSingleSelectBrandId === this.currentBrandId
        && this.localSelectedCategoryId === this.currentBrandCategoryId
        && isEqual(this.localSelectedSubcategoryIds, this.currentBrandSubcategoryIds);
    },
  },
  methods: {
    isDimensionInSearch(dimension) {
      return this.searchContains(dimension.category) || this.searchContains(dimension.key);
    },
    searchContains(str) {
      return str.toLowerCase().includes(this.searchTerm.toLowerCase());
    },
    async updateFilters() {
      this.updateSelectedCategoryAndSubcategories();

      this.$store.dispatch(
        'setDashboardSelectedFilters',
        this.localValue,
      );

      if (!this.showNewBrandSelector) await this.updateSelectedBrands();

      this.$emit('complete');
    },
    async updateSelectedBrands() {
      const brandFilter = this.localValue.find(filter => filter.key === 'brandName');

      if (brandFilter) {
        const updatedPortfolioBrands = getSelectedPortfolioBrands(
          this.portfolioBrands,
          brandFilter.value,
        );

        await this.$store.dispatch('setPortfolioBrands', updatedPortfolioBrands);
      }
    },
    updateSelectedCategoryAndSubcategories() {
      this.$store.commit(
        'updateBenchmarksCategory',
        this.localSelectedCategoryId,
      );
      this.$store.commit(
        'updateBenchmarksSubcategories',
        this.localSelectedSubcategoryIds,
      );
    },
    updateLocalSingleSelectBrandId({ brand, updateOtherDropdowns }) {
      this.localSingleSelectBrandId = brand.value;

      if (updateOtherDropdowns) {
        const benchmarksBrand = this.portfolioBrands.find(pb => pb.value === brand.value);

        this.updateSelectedCategory(
          {
            categoryId: benchmarksBrand.categoryId,
            subCategoryIds: benchmarksBrand.subCategoryIds,
            updateOtherDropdowns,
          },
        );
      }
    },
    updateSelectedCategory({ categoryId, subCategoryIds, updateOtherDropdowns }) {
      this.localSelectedCategoryId = categoryId;

      if (updateOtherDropdowns) {
        this.localSelectedSubcategoryIds = subCategoryIds || [];
      }
    },
    updateSelectedSubcategories(values) {
      this.localSelectedSubcategoryIds = values.map(v => v.value);
    },
    restoreDefaultFilters() {
      const defaultFilters = [
        {
          'key': 'category',
          'operator': 'includes',
          'value': [this.currentBrandCategoryId],
        },
        {
          'key': 'brandName',
          'operator': 'includes',
          'value': [this.currentBrandId],
        },
      ];

      this.localValue = defaultFilters;
      this.localSingleSelectBrandId = this.currentBrandId;
      this.localSelectedCategoryId = this.currentBrandCategoryId;
      this.localSelectedSubcategoryIds = this.currentBrandSubcategoryIds;
    },
  },
  mounted() {
    this.localValue = this.selectedFilters;
    this.localSingleSelectBrandId = this.currentBrandId;
    this.localSelectedCategoryId = this.selectedCategory;
    this.localSelectedSubcategoryIds = this.selectedSubcategories;
  },
  watch: {
    selectedFilters(newVal) {
      this.localValue = newVal;
    },
    activeBenchmarksView() {
      this.hasError = false;
    },
    show(isShowing) {
      if (isShowing) {
        this.localSelectedCategoryId = this.selectedCategory;
        this.localSelectedSubcategoryIds = this.selectedSubcategories;
      }
    },
  },
};
</script>
<style scoped lang="scss">
.filters-search {
  margin-bottom: 24px;
}

.no-filters-results {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #C1CCD4;
}

.common-slideout {
  ::v-deep .title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  ::v-deep .v-btn {
    text-transform: capitalize;
  }
}

</style>
